import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';
import { CopyRoundItem, CopyRoundParameters } from '../models/CopyRoundParameters';
import { Customer } from '../models/Customer';
import { Dispatch } from '../models/Dispatch';
import { DispatchItem } from '../models/DispatchItem';
import { FormStatus, LoadBankType } from '../models/Enums';
import { ErrorState, RoundFormState } from '../models/FormState';
import { ListItem } from '../models/ListItem';
import { LoadBankApplyParameters } from '../models/LoadBankApplyParameters';
import { LoadBankItem } from '../models/LoadBankItem';
import { Round } from '../models/Round';
import { RoundCompleteEvent } from '../models/RoundCompleteEvent';
import { RoundParameters } from '../models/RoundParameters';
import { ListDataService } from '../services/list-data.service';
import { RoundsDataService } from '../services/rounds-data.service';
import { ScheduleDataService } from '../services/schedule-data.service';

@Component({
    selector: 'copy-round',
    templateUrl: 'copy-round.component.html',
    styles: [
        `
        .bg-yellow{
            background: #fff2cc;
        }
      `
    ],
    standalone: false
})
export class CopyRoundComponent implements OnInit, OnChanges {
    constructor(private _listDataService: ListDataService, private _roundDataService: RoundsDataService, private _scheduleDataService: ScheduleDataService) { }

    @ViewChild('closeButton') closeButton;
    @ViewChild('truckerSearchDropdown') truckerSearchDropdownRef: ElementRef;
    @Output() saveComplete = new EventEmitter<number>();
    @Input() parameters: RoundParameters = new RoundParameters();
    @Input() copyRoundFormId: string = 'copyRoundModal';
    @Output() roundComplete: EventEmitter<RoundCompleteEvent> = new EventEmitter<RoundCompleteEvent>();

    roundParameters: RoundParameters = new RoundParameters();
    round: Round = new Round();

    loadBankApplyFormId: string = this.copyRoundFormId + 'LoadBankApply';
    loadBankApplyParameters: LoadBankApplyParameters = new LoadBankApplyParameters();
    loadBankType: LoadBankType;
    selectedIndex: number;
    dispatch: Dispatch = new Dispatch();

    readonly FormStatus = FormStatus;
    formStatus: FormStatus = FormStatus.Normal;
    formState: RoundFormState = new RoundFormState();
    showSaveComplete: boolean = false;
    errorMessage: string = '';

    truckerList: Customer[] = [];
    truckerSearchForm = this.createTruckerSearchForm();
    showTruckerSearch: boolean = false;

    planPeriods: ListItem[] = [];

    getPlanPeriods(): void {
        if (this.roundParameters.locCode.length == 0) {
            return;
        }

        this._listDataService.getPlanPeriods(this.roundParameters.locCode).subscribe({
            next: data => {
                this.planPeriods = data;
                if (this.planPeriods.length > 0) {
                    const planPeriodExists = this.planPeriods.some(item => item.value == this.roundParameters.planPeriod);
                    if (!planPeriodExists) {
                        this.roundParameters.planPeriod = this.planPeriods[0].value;
                    }
                    this.getPeriodDates(true);
                }
            },
            error: err => this.errorMessage = err
        });
    }

    trackPlanPeriod(index: number, item: ListItem) {
        return item.value;
    }

    getPeriodDates(refreshData: boolean): void {
        this._listDataService.getPeriodDates(this.roundParameters.planPeriod).subscribe({
            next: data => {
                this.roundParameters.periodStartDate = data.startDate;
                this.roundParameters.periodEndDate = data.endDate;
            },
            error: err => this.errorMessage = err
        });
    }

    planPeriodChange() {
        if (this.roundParameters.planPeriod != 'Custom') {
            this.getPeriodDates(false);
        }
    }


    createTruckerSearchForm() {
        const truckerSearchForm = new FormGroup({
            customerName: new FormControl(''),
            city: new FormControl(''),
            customerCode: new FormControl(''),
            isTrucker: new FormControl(true),
            customerId: new FormControl(0),
            preferredCurrency: new FormControl(''),
            hasCompliance: new FormControl(true),
            isCompliant: new FormControl(true)
        });

        truckerSearchForm.valueChanges
            .pipe(debounceTime(1000))
            .subscribe(() => {
                this.searchTruckers();
            });
        return truckerSearchForm;
    }

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const isTruckerSearchDropdown = this.truckerSearchDropdownRef?.nativeElement?.contains(target);

        if (!isTruckerSearchDropdown) {
            this.showTruckerSearch = false;
            this.clearTruckerSearch();
        }
    }

    toggleTruckerSearch(): void {
        if (this.showTruckerSearch) {
            this.showTruckerSearch = false;
            this.clearTruckerSearch();
        } else {
            this.showTruckerSearch = true;
        }
    }

    searchTruckers() {
        if (this.truckerSearchForm.controls.customerName?.value.length > 1 ||
            this.truckerSearchForm.controls.city?.value.length > 1 ||
            this.truckerSearchForm.controls.customerCode?.value.length > 1) {
            this._listDataService.searchCustomers(this.truckerSearchForm.getRawValue()).subscribe({
                next: data => {
                    this.truckerList = data;
                },
                error: err => this.errorMessage = err
            });
        }
        else {
            this.truckerList = [];
        }
    }

    clearTruckerSearch() {
        this.truckerSearchForm = this.createTruckerSearchForm();
        this.truckerList = [];
    }

    selectTrucker(trucker: Customer): void {
        this.round.truckerId = trucker.customerId;
        this.round.truckerName = trucker.customerName;
        this.validateTrucker();
    }

    isAnyLoadInvalid(): boolean {
        return this.round.dispatches.some(dispatch => parseInt(dispatch.loads) <= 0 || dispatch.loads == null);
    }

    applyLoadNumber(loadBankItem: LoadBankItem) {
        if (this.loadBankType == 0) {
            this.round.dispatches[this.selectedIndex].loadNumber = loadBankItem.loadNumber
        } else {
            this.round.dispatches[this.selectedIndex].unloadNumber = loadBankItem.loadNumber
        }
    }

    openLoadBank(loadBankType: LoadBankType, dispatch: DispatchItem, index: number) {
        this.loadBankType = loadBankType;
        this.selectedIndex = index;
        this.getDispatch(dispatch.dispatchId, dispatch.locCode, dispatch.commodityCode);
    }

    getDispatch(dispatchId: number, locCode: string, commodityCode: string): void {
        this._scheduleDataService.getDispatch(dispatchId).subscribe({
            next: data => {
                this.dispatch = data;
                let parameters = new LoadBankApplyParameters();
                parameters.parentModal = this.copyRoundFormId;
                parameters.locationId = this.dispatch.location.locationId;
                parameters.locationName = this.dispatch.location.locationName;
                parameters.customerId = this.dispatch.customer.customerId;
                parameters.customerName = this.dispatch.customer.customerName;
                if (this.dispatch.contract.contractId > 0) {
                    parameters.contractIds.push(this.dispatch.contract.contractId);
                    parameters.contractNumber = this.dispatch.contract.contractNumber;
                }
                parameters.locCode = locCode;
                parameters.loadBankType = this.loadBankType;
                parameters.commodityCode = commodityCode;
                parameters.dispatchId = dispatchId
                this.loadBankApplyParameters = parameters;
            },
            error: err => this.errorMessage = err
        });
    }


    validateTrucker() {
        this.formState.trucker = new ErrorState();
        if (this.formState.submitted) {
            if (this.round.truckerId == 0) {
                this.formState.trucker.invalid = true;
                this.formState.trucker.errorMessage = 'Trucker is required';
            }
        }
    }

    validatePeriodDates() {
        this.formState.periodDates = new ErrorState();
        const startDate = new Date(this.roundParameters.periodStartDate);
        const endDate = new Date(this.roundParameters.periodEndDate);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || startDate.getTime() > endDate.getTime()) {
            this.formState.periodDates.invalid = true;
            this.formState.periodDates.errorMessage = 'Invalid period dates entered'
        }
    }

    validateForm() {
        this.validateTrucker();
        this.validatePeriodDates();
    }

    private saveClick = new Subject<boolean>();

    onSaveClick(closeForm: boolean = false) {
        this.saveClick.next(closeForm);
    }

    saveRoundCopy(closeForm: boolean) {
        this.formState.submitted = true;
        this.validateForm();

        if (!this.formState.isValid()) {
            return;
        }
       
        this.formStatus = FormStatus.Saving;
        const copyParams = this.mapCopyRoundParameters();

        this._roundDataService.copyRound(copyParams).subscribe({
            next: data => {
                this.formStatus = FormStatus.Normal;
                let eventParms = new RoundCompleteEvent();
                eventParms.scheduleIds = data;
               
                if (closeForm) {
                    eventParms.message = "Round was saved successfully";
                    this.roundComplete.emit(eventParms);
                    this.closeButton.nativeElement.click();
                }
                else {
                    this.roundComplete.emit(eventParms);
                    this.showSaveComplete = true;
                    setTimeout(() => {
                        this.showSaveComplete = false;
                    }, 3000);
                    this.resetComponent();
                    this.getRound();
                }
            },
            error: err => {
                this.errorMessage = err;
                this.formStatus = FormStatus.Normal;
            }
        });
    }

    private mapCopyRoundParameters() {
        const params = new CopyRoundParameters();
        params.roundId = this.round.roundId;
        params.truckerId = this.round.truckerId;
        params.roundNotes = this.round.notes;
        params.internalComments = this.round.internalComments;
        params.periodStartDate = this.roundParameters.periodStartDate;
        params.periodEndDate = this.roundParameters.periodEndDate;
        params.dispatches = this.round.dispatches.map(x => {
            const d = new CopyRoundItem();
            d.dispatchId = x.dispatchId;
            d.loads = Number(x.loads);
            d.loadNumber = x.loadNumber;
            d.unloadNumber = x.unloadNumber;
            d.rate = x.rate;
            return d;
        })
        return params;
    }

    getRound() {
        if (this.roundParameters.roundId > 0) {
            this._roundDataService.getRound(this.roundParameters.roundId).subscribe({
                next: data => {
                    this.round = data;
                    this.round.truckerId = 0;
                    this.round.truckerName = '';
                },
                error: err => this.errorMessage = err
            });
        }
    } 

    resetComponent() {
        this.formStatus = FormStatus.Normal;
        this.formState = new RoundFormState();
        this.round = new Round();
        this.clearTruckerSearch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['parameters']) {
            this.roundParameters = { ...this.parameters };
            this.resetComponent();
            this.getPlanPeriods();
            this.getRound();
        }

        if (changes['copyRoundFormId']) {
            this.loadBankApplyFormId = this.copyRoundFormId + 'LoadBankApply';
        }
    }

    ngOnInit(): void {
        //prevent double click from calling save twice
        this.saveClick.pipe(
            debounceTime(300)
        ).subscribe((closeForm: boolean) => this.saveRoundCopy(closeForm));
    }

}